import { useSyncExternalStore } from 'react';
import { ApiError } from './api.js';
import type { Outbox, OutboxEntry } from './outbox.js';

/**
 * Briefing „Seitenleisten“ S3 (ADR 0175): objektbezogener Speicherstatus. KEINE zweite Speicherengine – die
 * fachlichen Wege bleiben (Ausgangskorb, SerialSaver, direkte Aufrufe mit Bestätigung). Diese Schicht registriert nur
 * deren Zustand je Inhalt (`task:<id>`, `file:<id>`, `page:<id>` …) und Vorgang und fasst ihn für den Kopf zusammen.
 *
 * Regeln:
 * - „Gespeichert“ erst, wenn für den Inhalt nichts mehr offen ist und mindestens eine eigene Änderung bestätigt wurde.
 * - Fehler/Konflikte bleiben stehen, bis sie behoben (erneut versucht, erfolgreich nachgespeichert) oder bewusst
 *   ausgeblendet werden – eine spätere Erfolgsmeldung eines ANDEREN Vorgangs verdeckt sie nicht.
 * - Priorität: Konflikt/Fehler > Übertragung ausstehend/offline > speichert > gespeichert > unverändert.
 * - Fachliche Handlungen (Kommentar senden, Abnahme, Buchung, Upload) melden sich hier nicht als „gespeichert“.
 */
/** `waiting`: Entwurf dauerhaft auf dem Gerät, Übertragung später (eigener Weg, z. B. SerialSaver offline) */
/** `dirty`: Eingabe geändert, aber noch kein Speichervorgang geplant (R01: „Änderung offen“, nie „Gespeichert“) */
export type OpState = 'dirty' | 'saving' | 'waiting' | 'saved' | 'failed' | 'conflict';
export interface SaveOp {
  /** Vorgang innerhalb des Inhalts, z. B. `title`, `description`, `fields` – gleiche ID ersetzt den alten Stand */
  id: string;
  label: string;
  state: OpState;
  reason?: string;
  retry?: () => void;
  /**
   * R04: liegt die Eingabe dauerhaft auf dem Gerät (Entwurf/Ausgangskorb)? `false` = nur im Arbeitsspeicher – dann
   * nie „lokal gesichert“ zusagen, sondern „Nicht gesichert · geöffnet lassen“ und Kopieren anbieten.
   */
  durable?: boolean;
  /** Text zum Kopieren, solange er nirgends dauerhaft liegt */
  copy?: () => string;
  /** Entwurf bewusst verwerfen (z. B. Saver auf den Serverstand zurücksetzen) – getrennt von „Ausblenden“ */
  discard?: () => void;
  /** Meldung ausgeblendet; ein ungesicherter Entwurf bleibt trotzdem geschützt (Warnung, globaler Hinweis) */
  hidden?: boolean;
  /**
   * R03: Sendeversuch. Eine Antwort mit kleinerer Generation als der zuletzt gestartete Versuch desselben Vorgangs
   * wird ignoriert – eine alte Antwort bestätigt nie eine neuere Änderung und löscht nie deren Fehler.
   */
  gen?: number;
  at: number;
}
export type Aggregate = 'idle' | 'dirty' | 'saving' | 'saved' | 'pending' | 'offline' | 'local-only' | 'failed' | 'conflict';
export interface ContentStatus {
  state: Aggregate;
  /** ungelöste Fehler/Konflikte (für das Status-Detail) */
  problems: SaveOp[];
  /** offene Vorgänge (Ausgangskorb + laufend) */
  open: number;
  /** R04: Eingaben, die nur im Arbeitsspeicher liegen (zum Kopieren anbieten) */
  unsafe: SaveOp[];
}

type Listener = () => void;
const ops = new Map<string, Map<string, SaveOp>>();
const confirmed = new Set<string>();
/** zuletzt gestartete Generation je Inhalt+Vorgang (R03) */
const gens = new Map<string, number>();
let trackGen = 0;
/** Projekt je Inhalt – für „Aufgabe öffnen“ aus dem globalen Hinweis nach dem Schließen */
const projectOf = new Map<string, string>();
let problemCache: { key: string; projectId: string | null; labels: string[]; ops: SaveOp[] }[] | null = null;
const listeners = new Set<Listener>();
let box: Outbox | null = null;
let cache = new Map<string, ContentStatus>();
const emit = () => {
  cache = new Map();
  problemCache = null;
  listeners.forEach((l) => l());
};

/** Vorgänge, die als ausdrückliche Handlung eigene Rückmeldung haben (A12) */
const EXPLICIT = /^(comment|approval|attachment|talk|time|budget)/;

const LABELS: Record<string, string> = {
  title: 'Titel',
  description: 'Beschreibung',
  statusId: 'Status',
  completed: 'Erledigt',
  completedAt: 'Erledigt',
  dueDate: 'Frist',
  startDate: 'Beginn',
  priority: 'Priorität',
  repeat: 'Wiederholung',
  milestone: 'Meilenstein',
  accent: 'Farbe',
  cover: 'Titelbild',
  boardPosition: 'Stelle im Board',
};
const KIND_LABEL: Record<string, string> = {
  'task.assignees': 'Zuständige',
  'task.labels': 'Labels',
  'task.fields': 'Eigene Felder',
  'task.dependencies': 'Abhängigkeiten',
  'task.move': 'Liste/Abschnitt',
  'task.board': 'Stelle im Board',
  'task.pin': 'Anheften',
  'detail.check': 'Checkliste',
};

/** Zu welchem Inhalt gehört ein Eintrag des Ausgangskorbs? */
export function contentOf(e: Pick<OutboxEntry, 'kind' | 'url' | 'overlay' | 'meta'> & { body?: unknown }): string | null {
  if (EXPLICIT.test(e.kind)) return null;
  // Verknüpfungen gehören zum Inhalt, an dem sie gesetzt wurden (Nachprüfung 0.167.1: Abdeckung über Aufgaben hinaus)
  if (e.kind === 'link.set') {
    const from = (e.body as { from?: { kind?: string; id?: string } } | undefined)?.from;
    return from?.kind && from.id ? `${from.kind}:${from.id}` : null;
  }
  const o = e.overlay as Record<string, unknown> | undefined;
  if (o) {
    if (typeof o.taskId === 'string') return `task:${o.taskId}`;
    const item = o.item as { taskId?: string } | undefined;
    if (item?.taskId) return `task:${item.taskId}`;
    if (typeof o.fileId === 'string' && o.type === 'filePatch') return `file:${o.fileId}`;
    if (typeof o.pageId === 'string' && o.type === 'pagePatch') return `page:${o.pageId}`;
  }
  const m = /^\/api\/(tasks|files|pages|events|people|decisions)\/([0-9a-f-]{36})(?:\/|$|\?)/.exec(e.url);
  if (m && !/\/(comments|attachments|reactions)/.test(e.url)) return `${{ tasks: 'task', files: 'file', pages: 'page', events: 'event', people: 'contact', decisions: 'decision' }[m[1]!]}:${m[2]}`;
  const meta = e.meta as { taskId?: string; base?: { id?: string } } | undefined;
  if (meta?.taskId) return `task:${meta.taskId}`;
  return null;
}

/** Vorgangs-ID und Beschriftung eines Eintrags (je Feld, damit ein späterer Erfolg eines anderen Felds nichts verdeckt) */
export function opOf(e: Pick<OutboxEntry, 'kind' | 'body' | 'overlay'>): { id: string; label: string } {
  const patch = (e.body as { patch?: Record<string, unknown> } | undefined)?.patch;
  if (patch && Object.keys(patch).length) {
    const keys = Object.keys(patch).sort();
    return { id: `${e.kind}:${keys.join(',')}`, label: keys.map((k) => LABELS[k] ?? k).join(', ') };
  }
  const fields = (e.overlay as { fields?: Record<string, unknown> } | undefined)?.fields;
  if (e.kind === 'task.update' && fields) {
    const keys = Object.keys(fields).sort();
    return { id: `${e.kind}:${keys.join(',')}`, label: keys.map((k) => LABELS[k] ?? k).join(', ') };
  }
  return { id: e.kind, label: KIND_LABEL[e.kind] ?? (e.kind.startsWith('check') || e.kind.startsWith('detail') ? 'Checkliste' : 'Änderung') };
}

function reasonOf(err: unknown): { state: OpState; reason: string } {
  if (err instanceof ApiError && err.code === 'version_conflict') return { state: 'conflict', reason: 'Inzwischen von jemand anderem geändert.' };
  if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return { state: 'conflict', reason: err.status === 403 ? 'Keine Berechtigung mehr.' : 'Gibt es nicht mehr.' };
  return { state: 'failed', reason: err instanceof Error ? err.message : 'Unbekannter Fehler.' };
}

/** Vorgang setzen (direkte Wege, SerialSaver). `saved` entfernt einen offenen/fehlgeschlagenen Vorgang derselben ID. */
export function setOp(key: string, op: Omit<SaveOp, 'at'>, projectId?: string) {
  if (op.gen !== undefined) {
    const g = `${key}\u0000${op.id}`;
    if (op.gen < (gens.get(g) ?? -Infinity)) return; // verspätete Antwort eines älteren Versuchs
    gens.set(g, op.gen);
  }
  if (projectId) projectOf.set(key, projectId);
  const m = ops.get(key) ?? new Map<string, SaveOp>();
  if (op.state === 'saved') {
    m.delete(op.id);
    confirmed.add(key);
  } else m.set(op.id, { ...op, at: Date.now() });
  if (m.size) ops.set(key, m);
  else ops.delete(key);
  emit();
}

/**
 * „Ausblenden“ (Nachprüfung 0.167.2, Punkt 1): blendet nur die MELDUNG aus. Liegt die Eingabe nirgends dauerhaft,
 * bleibt der Eintrag als ungesicherter Entwurf bestehen – mit Warnung beim Verlassen und im globalen Hinweis.
 */
export function hideOp(key: string, id: string) {
  const op = ops.get(key)?.get(id);
  if (!op) return;
  if (op.durable === false) {
    ops.get(key)!.set(id, { ...op, hidden: true });
    emit();
  } else dismissOp(key, id);
}

/** Entwurf bewusst verwerfen: eigene Aktion, nie Nebenwirkung von „Ausblenden“ */
export function discardOp(key: string, id: string) {
  ops.get(key)?.get(id)?.discard?.();
  dismissOp(key, id);
}

/** Vorgang ohne Bestätigung auflösen (intern, z. B. Titel zurück auf den bestätigten Wert, R01) */
export function dismissOp(key: string, id: string) {
  const m = ops.get(key);
  if (!m?.has(id)) return;
  m.delete(id);
  if (!m.size) ops.delete(key);
  emit();
}

/** Direkten Speicheraufruf verfolgen – erst die Serverantwort bestätigt */
export function track<T>(key: string, id: string, label: string, p: Promise<T>, opts: { retry?: () => void; projectId?: string } = {}): Promise<T> {
  const gen = ++trackGen;
  setOp(key, { id, label, state: 'saving', gen }, opts.projectId);
  p.then(
    () => setOp(key, { id, label, state: 'saved', gen }),
    (err) => setOp(key, { id, label, ...reasonOf(err), retry: opts.retry, gen }),
  );
  return p;
}

/** Ausgangskorb anbinden: offene Einträge zählen, Ergebnisse je Vorgang festhalten */
export function connectOutbox(o: Outbox) {
  if (box === o) return;
  box = o;
  let user = o.getUser();
  o.subscribe(() => {
    // Konto gewechselt/abgemeldet: keine Bestätigung oder Fehlermeldung einer anderen Person übernehmen
    if (o.getUser() !== user) {
      user = o.getUser();
      ops.clear();
      confirmed.clear();
      gens.clear();
    }
    emit();
  });
  o.observe((e, ok, err) => {
    const key = contentOf(e);
    if (!key) return;
    const { id, label } = opOf(e);
    // Generation = Zeitpunkt des Eintrags: eine spätere Antwort auf einen älteren Eintrag verdeckt nichts Neueres
    if (ok) setOp(key, { id, label, state: 'saved', gen: e.createdAt }, e.projectId);
    else setOp(key, { id, label, ...reasonOf(err), gen: e.createdAt }, e.projectId);
  });
}

/** Abmelden/anderes Konto: nichts von vorher übernehmen */
export function resetSaveStatus() {
  ops.clear();
  confirmed.clear();
  gens.clear();
  emit();
}

export function statusOf(key: string): ContentStatus {
  const hit = cache.get(key);
  if (hit) return hit;
  const mine = [...(ops.get(key)?.values() ?? [])];
  const problems = mine.filter((x) => (x.state === 'failed' || x.state === 'conflict') && !x.hidden).sort((a, b) => a.at - b.at);
  const queued = box ? box.entries().filter((e) => contentOf(e) === key) : [];
  const saving = mine.filter((x) => x.state === 'saving').length;
  const waitingOps = mine.filter((x) => x.state === 'waiting').length;
  const dirty = mine.filter((x) => x.state === 'dirty').length;
  // R04: offene Eingabe, die nirgends dauerhaft liegt – nie „lokal gesichert“ melden
  const unsafeOps = mine.filter((x) => x.durable === false);
  const unsafe = unsafeOps.length > 0;
  const status = box?.getStatus();
  let state: Aggregate;
  if (problems.some((x) => x.state === 'conflict')) state = 'conflict';
  else if (problems.length) state = 'failed';
  else if (unsafe) state = 'local-only';
  else if (waitingOps) state = typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'pending';
  else if (queued.length && status?.waiting) state = typeof navigator !== 'undefined' && navigator.onLine === false ? (box?.volatile ? 'local-only' : 'offline') : 'pending';
  else if (queued.length && box?.volatile) state = 'local-only';
  else if (dirty) state = 'dirty';
  else if (queued.length || saving) state = 'saving';
  else state = confirmed.has(key) ? 'saved' : 'idle';
  const out = { state, problems, open: queued.length + saving + waitingOps + dirty, unsafe: unsafeOps };
  cache.set(key, out);
  return out;
}

const subscribe = (l: Listener) => {
  listeners.add(l);
  const on = () => emit();
  window.addEventListener('online', on);
  window.addEventListener('offline', on);
  return () => {
    listeners.delete(l);
    window.removeEventListener('online', on);
    window.removeEventListener('offline', on);
  };
};

export function useContentStatus(key: string | null): ContentStatus {
  return useSyncExternalStore(subscribe, () => (key ? statusOf(key) : IDLE));
}
const IDLE: ContentStatus = { state: 'idle', problems: [], open: 0, unsafe: [] };

/** Alle Inhalte mit ungelösten Problemen (globaler Hinweis nach dem Schließen, A09) */
export function problemList() {
  if (problemCache) return problemCache;
  // Punkt 2: auch ungesicherte Entwürfe (offline wartend, nur im Arbeitsspeicher, ausgeblendete Fehler) bleiben erreichbar
  problemCache = [...ops.entries()]
    .map(([key, m]) => {
      const list = [...m.values()].filter((x) => ((x.state === 'failed' || x.state === 'conflict') && !x.hidden) || x.durable === false);
      return { key, projectId: projectOf.get(key) ?? null, labels: list.map((x) => x.label), ops: list };
    })
    .filter((x) => x.labels.length);
  return problemCache;
}
export const useProblems = () => useSyncExternalStore(subscribe, problemList);

/** R04: gibt es Eingaben, die nur im Arbeitsspeicher liegen? (Warnung beim Verlassen der Seite) */
export function hasUnsafe(): boolean {
  for (const m of ops.values()) for (const op of m.values()) if (op.durable === false) return true;
  return false;
}
